import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faArrowRight,
  faFilter,
  faTimes,
  faEye,
  faImages,
  faExternalLinkAlt,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { getProjets } from "../../services/api";
import { Projet } from "../../types/types";
import { useLanguage } from "../../context/LanguageContext";
import { getTranslation } from "../../utils/translations";
import { resolveMediaUrl } from "../../utils/media";

const ProjectsPage: React.FC = () => {
  const [projets, setProjets] = useState<Projet[]>([]);
  const [filteredProjets, setFilteredProjets] = useState<Projet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProject, setSelectedProject] = useState<Projet | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { language } = useLanguage();

  useEffect(() => {
    fetchProjets();
  }, []);

  useEffect(() => {
    filterProjects();
  }, [projets, searchTerm, language]);

  const fetchProjets = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getProjets();

      const projetsData = response.data.map(
        (projet: any): Projet => ({
          id: projet.id,
          titrearabe: projet.titrearabe || "",
          titrefrancais: projet.titrefrancais || "",
          descarabe: projet.descarabe || "",
          descfrancais: projet.descfrancais || "",
          lien: projet.lien || "",
          images: Array.isArray(projet.images)
            ? projet.images.map((img: any) => ({
                image: img.image || "",
              }))
            : [],
        })
      );

      setProjets(projetsData);
    } catch (error: any) {
      console.error("Error fetching projects:", error);
      setError(getTranslation("errorLoadingProjects", language));
    } finally {
      setLoading(false);
    }
  };

  const filterProjects = () => {
    let filtered = [...projets];

    if (searchTerm) {
      filtered = filtered.filter(
        (p) =>
          p.titrearabe?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.titrefrancais?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.descarabe?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.descfrancais?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredProjets(filtered);
  };

  const getFirstImage = (images: Array<{ image: string }> = []) => {
    if (!images || images.length === 0) return null;
    try {
      return images[0].image;
    } catch (err) {
      console.error("Error parsing image:", err);
      return null;
    }
  };

  const openProjectModal = (project: Projet) => {
    setSelectedProject(project);
    setCurrentImageIndex(0);
    setIsModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
    document.body.style.overflow = "auto";
  };

  const nextImage = () => {
    if (selectedProject && selectedProject.images) {
      setCurrentImageIndex((prev) =>
        prev === selectedProject.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (selectedProject && selectedProject.images) {
      setCurrentImageIndex((prev) =>
        prev === 0 ? selectedProject.images.length - 1 : prev - 1
      );
    }
  };

  // Get appropriate title based on language
  const getTitle = (titrearabe?: string, titrefrancais?: string) => {
    return language === "ar"
      ? titrearabe || titrefrancais || getTranslation("noTitle", language)
      : titrefrancais || titrearabe || getTranslation("noTitle", language);
  };

  // Get appropriate description based on language
  const getDescription = (descarabe?: string, descfrancais?: string) => {
    return language === "ar"
      ? descarabe || descfrancais || getTranslation("noDescription", language)
      : descfrancais || descarabe || getTranslation("noDescription", language);
  };

  // Direction and text alignment
  const getDirectionClass = () => (language === "ar" ? "rtl" : "ltr");
  const getTextAlignClass = () =>
    language === "ar" ? "text-right" : "text-left";

  if (error) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center ${getDirectionClass()}`}
      >
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {getTranslation("error", language)}
          </h2>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={fetchProjets}
            className="mt-4 px-6 py-2 bg-mauritania-green text-white rounded-lg hover:bg-mauritania-green-dark transition duration-300"
          >
            {getTranslation("retry", language)}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={getDirectionClass()}>
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-mauritania-green to-mauritania-green-dark text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl font-bold mb-6">
              {getTranslation("ministryProjects", language)}
            </h1>
            <p className="text-xl opacity-90 mb-8">
              {getTranslation("discoverDevelopmentProjects", language)}
            </p>

            {/* Search Bar */}
            <div className="relative max-w-2xl mx-auto">
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
              </div>
              <input
                type="text"
                className={`w-full pr-10 pl-3 py-4 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 ${
                  language === "ar" ? "text-right" : "text-left"
                }`}
                placeholder={getTranslation("searchProjects", language)}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Projects Section */}
      <div className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          {/* Search Results */}
          <div className="mb-8 flex justify-between items-center">
            <h2
              className={`text-2xl font-bold text-gray-800 ${getTextAlignClass()}`}
            >
              {getTranslation("allProjects", language)}
              <span className="text-mauritania-green mr-2">
                ({filteredProjets.length})
              </span>
            </h2>

            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="text-red-600 hover:text-red-800 flex items-center"
              >
                <FontAwesomeIcon icon={faTimes} className="ml-2" />
                {getTranslation("clearSearch", language)}
              </button>
            )}
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-16">
              <FontAwesomeIcon
                icon={faSpinner}
                className="text-mauritania-green-dark text-4xl animate-spin mb-4"
              />
              <p className="text-gray-600">
                {getTranslation("loadingProjects", language)}
              </p>
            </div>
          )}

          {/* Projects Grid */}
          {!loading && filteredProjets.length === 0 ? (
            <div
              className={`text-center py-16 bg-white rounded-xl shadow-sm ${getTextAlignClass()}`}
            >
              <div className="max-w-md mx-auto">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="text-gray-400 text-5xl mb-4"
                />
                <h3 className="text-xl font-bold text-gray-700 mb-2">
                  {searchTerm
                    ? getTranslation("noResults", language)
                    : getTranslation("noProjects", language)}
                </h3>
                <p className="text-gray-600">
                  {searchTerm
                    ? getTranslation("noMatchingProjects", language)
                    : getTranslation("noProjectsAvailable", language)}
                </p>
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="mt-4 px-4 py-2 bg-mauritania-green text-white rounded-lg hover:bg-mauritania-green-dark transition duration-300"
                  >
                    {getTranslation("viewAllProjects", language)}
                  </button>
                )}
              </div>
            </div>
          ) : (
            !loading && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProjets.map((projet) => {
                  const firstImage = getFirstImage(projet.images);

                  return (
                    <div
                      key={projet.id}
                      className={`bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 ${getTextAlignClass()}`}
                    >
                      {/* Image */}
                      <div className="h-48 bg-gray-200 relative">
                        {firstImage ? (
                          <img
                            src={resolveMediaUrl(firstImage)}
                            alt={getTitle(
                              projet.titrearabe,
                              projet.titrefrancais
                            )}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                              const parent = e.currentTarget.parentElement;
                              if (parent) {
                                parent.innerHTML = `
                                  <div class="w-full h-full flex items-center justify-center bg-gray-100">
                                    <i class="fas fa-images text-gray-400 text-4xl"></i>
                                  </div>
                                `;
                              }
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gray-100">
                            <FontAwesomeIcon
                              icon={faImages}
                              className="text-gray-400 text-4xl"
                            />
                          </div>
                        )}

                        {projet.images && projet.images.length > 0 && (
                          <div className="absolute top-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                            <FontAwesomeIcon icon={faImages} className="ml-1" />
                            {projet.images.length}
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="p-4">
                        <h3 className="font-bold text-lg text-gray-900 mb-2">
                          {getTitle(projet.titrearabe, projet.titrefrancais)}
                        </h3>

                        <div
                          className="text-sm text-gray-500 mb-4 line-clamp-3"
                          dangerouslySetInnerHTML={{
                            __html:
                              getDescription(
                                projet.descarabe,
                                projet.descfrancais
                              ).substring(0, 150) +
                              (getDescription(
                                projet.descarabe,
                                projet.descfrancais
                              ).length > 150
                                ? "..."
                                : ""),
                          }}
                        />

                        {/* Actions */}
                        <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                          <button
                            onClick={() => openProjectModal(projet)}
                            className="text-mauritania-green hover:text-mauritania-green-dark font-semibold text-sm flex items-center"
                          >
                            {getTranslation("details", language)}
                            <FontAwesomeIcon
                              icon={faArrowRight}
                              className={`${
                                language === "ar" ? "mr-2 rotate-180" : "ml-2"
                              }`}
                            />
                          </button>

                          {projet.lien && (
                            <a
                              href={projet.lien}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                              title={getTranslation("externalLink", language)}
                            >
                              <FontAwesomeIcon
                                icon={faExternalLinkAlt}
                                className={`text-xs ${
                                  language === "ar" ? "ml-1" : "mr-1"
                                }`}
                              />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}
        </div>
      </div>

      {/* Project Detail Modal */}
      {isModalOpen && selectedProject && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
          <div
            className={`bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden ${getDirectionClass()}`}
          >
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h2
                className={`text-xl font-bold text-gray-900 ${getTextAlignClass()}`}
              >
                {getTitle(
                  selectedProject.titrearabe,
                  selectedProject.titrefrancais
                )}
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                &times;
              </button>
            </div>

            {/* Content */}
            <div className="overflow-y-auto max-h-[70vh] p-6">
              {/* Images Gallery */}
              {selectedProject.images && selectedProject.images.length > 0 && (
                <div className="mb-8">
                  <div className="relative h-64 rounded-lg overflow-hidden mb-4">
                    <img
                      src={resolveMediaUrl(
                        selectedProject.images[currentImageIndex].image
                      )}
                      alt={getTitle(
                        selectedProject.titrearabe,
                        selectedProject.titrefrancais
                      )}
                      className="w-full h-full object-cover"
                    />

                    {selectedProject.images.length > 1 && (
                      <>
                        <button
                          onClick={prevImage}
                          className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 p-2 rounded-full hover:bg-opacity-100 transition"
                        >
                          {language === "ar" ? "→" : "←"}
                        </button>
                        <button
                          onClick={nextImage}
                          className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 p-2 rounded-full hover:bg-opacity-100 transition"
                        >
                          {language === "ar" ? "←" : "→"}
                        </button>
                      </>
                    )}
                  </div>

                  {selectedProject.images.length > 1 && (
                    <div
                      className={`text-center text-sm text-gray-600 ${getTextAlignClass()}`}
                    >
                      {language === "ar"
                        ? `صورة ${currentImageIndex + 1} من ${
                            selectedProject.images.length
                          }`
                        : `Image ${currentImageIndex + 1} sur ${
                            selectedProject.images.length
                          }`}
                    </div>
                  )}
                </div>
              )}

              {/* Project Details */}
              <div className={`mb-6 ${getTextAlignClass()}`}>
                <h3 className="text-lg font-bold text-gray-800 mb-2">
                  {language === "ar" ? "الوصف" : "Description"}
                </h3>
                <div
                  className="text-gray-700 leading-relaxed"
                  dangerouslySetInnerHTML={{
                    __html: getDescription(
                      selectedProject.descarabe,
                      selectedProject.descfrancais
                    ),
                  }}
                />
              </div>

              {selectedProject.lien && (
                <div
                  className={`bg-blue-50 p-4 rounded-lg ${getTextAlignClass()}`}
                >
                  <h4 className="font-bold text-blue-800 mb-2">
                    {getTranslation("externalLink", language)}
                  </h4>
                  <a
                    href={selectedProject.lien}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 break-all"
                  >
                    {selectedProject.lien}
                  </a>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={closeModal}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition duration-300"
              >
                {getTranslation("close", language)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectsPage;
